// src/components/auth/SignUp.js
//
// Chunk 9b — i18n stripped, restyled to match design system.
//
// Preserved verbatim:
//   • The signup flow (supabase.auth.signUp, invite token handling)
//   • updateProfileWithRetry for partner invites
//   • fetchInviterInfo
//   • Confirmation screen + invite link/connect flow
//
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { theme } from '../../lib/theme'

export default function SignUp() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [inviteToken, setInviteToken] = useState(null)
  const [inviterName, setInviterName] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('invite')
    if (token) {
      setInviteToken(token)
      fetchInviterInfo(token)
    }
  }, [])

  const fetchInviterInfo = async (token) => {
    const { data } = await supabase
      .from('partner_invites')
      .select('*, profiles!partner_invites_inviter_id_fkey(full_name)')
      .eq('token', token)
      .eq('status', 'pending')
      .single()
    if (data?.profiles?.full_name) {
      setInviterName(data.profiles.full_name)
      if (data.invitee_email) setEmail(data.invitee_email)
    }
  }

  const updateProfileWithRetry = async (userId, updates, maxAttempts = 8) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 800))
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single()
      if (existing?.id) {
        await supabase
          .from('profiles')
          .update(updates)
          .eq('id', userId)
        return true
      }
    }
    return false
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
      },
    })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    if (inviteToken && data?.user) {
      await supabase.rpc('accept_partner_invite', {
        p_token: inviteToken,
        p_user_id: data.user.id,
      })
      await updateProfileWithRetry(data.user.id, {
        onboarding_completed: true,
        onboarding_step: 5,
        subscription_status: 'partner',
      })
    }

    setConfirmed(true)
    setLoading(false)
  }

  if (confirmed) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <span style={styles.logoMerq}>Merq</span>
            <span style={styles.logoVault}>Vault</span>
          </div>
          <h2 style={styles.successTitle}>Check your email</h2>
          <p style={styles.successText}>
            We sent a confirmation link to <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{email}</strong>.
            Click it to activate your account.
          </p>
          {inviteToken && (
            <div style={styles.successInvite}>
              <span>
                Once confirmed, you'll be connected to {inviterName || 'your partner'}'s account and can start sharing finances.
              </span>
            </div>
          )}
          <p style={styles.successSub}>
            Already confirmed? Sign in below.
          </p>
          <Link to="/login" style={styles.button}>
            Go to sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoMerq}>Merq</span>
          <span style={styles.logoVault}>Vault</span>
        </div>

        {inviteToken ? (
          <div style={styles.inviteBadge}>
            <span>
              {inviterName ? `${inviterName} invited you` : 'You were invited'} to share finances on Merq Vault
            </span>
          </div>
        ) : (
          <>
            <p style={styles.tagline}>
              Privacy-first household budgeting.
            </p>
            <div style={styles.trialBadge}>
              <span>45 days free — no credit card required</span>
            </div>
          </>
        )}

        <form onSubmit={handleSignUp}>
          <div style={styles.field}>
            <label style={styles.label}>Full name</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Your name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="Min 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            type="submit"
            disabled={loading}
          >
            {loading
              ? 'Creating account…'
              : inviteToken
                ? 'Create account & connect'
                : 'Start free trial'}
          </button>
        </form>

        <div style={styles.legal}>
          By creating an account you agree to our{' '}
          <a href="https://getmerq.com/terms.html" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Terms of Service</a>
          {' '}and{' '}
          <a href="https://getmerq.com/privacy.html" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Privacy Policy</a>.
        </div>

        <div style={styles.login}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Sign in</Link>
        </div>
      </div>
    </div>
  )
}

const c = theme.colors
const f = theme.fonts

const styles = {
  container: {
    minHeight: '100vh',
    background: c.cream,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    fontFamily: f.body,
  },
  card: {
    background: c.white,
    borderRadius: '20px',
    padding: '40px 32px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 14px 50px rgba(30, 16, 64, 0.1)',
    textAlign: 'center',
    fontFamily: f.body,
  },
  logo: {
    fontSize: '30px', fontWeight: 700,
    marginBottom: '20px',
    display: 'flex', alignItems: 'baseline',
    justifyContent: 'center', gap: '6px',
    letterSpacing: '-0.01em',
  },
  logoMerq: { color: c.plum },
  logoVault: {
    color: c.green,
    fontFamily: f.display,
    fontStyle: 'italic',
  },
  tagline: {
    color: c.inkMuted,
    marginBottom: '14px',
    fontSize: '14px',
  },
  trialBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: c.greenSoft, color: c.green,
    padding: '8px 14px',
    borderRadius: '999px',
    fontSize: '12.5px', fontWeight: 600,
    marginBottom: '24px',
  },
  inviteBadge: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: c.greenSoft, color: c.green,
    padding: '12px 14px', borderRadius: '10px',
    fontSize: '13px', fontWeight: 600,
    marginBottom: '24px',
    textAlign: 'left',
    lineHeight: 1.4,
  },
  field: { marginBottom: '14px', textAlign: 'left' },
  label: {
    display: 'block',
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14.5px',
    outline: 'none', boxSizing: 'border-box',
    color: c.plum,
  },
  error: {
    background: c.roseSoft, color: c.rose,
    padding: '10px 12px', borderRadius: '8px',
    fontSize: '13px', marginBottom: '14px',
    textAlign: 'left',
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '13px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '12px',
    fontFamily: f.body, fontSize: '14.5px', fontWeight: 600,
    cursor: 'pointer',
    marginTop: '6px',
    textDecoration: 'none',
    boxSizing: 'border-box',
  },
  legal: {
    textAlign: 'center', marginTop: '16px',
    fontSize: '11.5px', color: c.inkMuted,
    lineHeight: 1.55,
  },
  legalLink: {
    color: c.green, textDecoration: 'none',
    fontWeight: 600,
  },
  login: {
    textAlign: 'center', marginTop: '12px',
    fontSize: '13px', color: c.inkMuted,
  },
  link: { color: c.green, textDecoration: 'none', fontWeight: 600 },

  successIcon: {
    width: '64px', height: '64px',
    borderRadius: '18px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 18px',
  },
  successTitle: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '14px', color: c.inkMuted,
    lineHeight: 1.6, marginBottom: '14px',
  },
  successInvite: {
    display: 'flex', alignItems: 'flex-start', gap: '8px',
    background: c.greenSoft, color: c.green,
    padding: '12px 14px', borderRadius: '10px',
    fontSize: '12.5px', fontWeight: 500,
    marginBottom: '14px', lineHeight: 1.5,
    textAlign: 'left',
  },
  successSub: {
    fontSize: '13px', color: c.inkMuted,
    marginBottom: '20px',
  },
}
